/**
 * data/instrumentCatalog/forex.ts
 * Forex currency pairs reference catalog
 * 
 * Contains actively traded forex pairs organized by G10/EM/Other classification
 * with accurate pip values and proper asset class taxonomy.
 * 
 * Research file: docs/reference/InstrumentCatalogResearch/Forex_DataPopulationResearch.md
 * 
 * NEW STRUCTURE: Uses contractSize, contractAsset, incrementSize, incrementType, quoteCurrency
 * for clear, consistent specifications across all asset classes.
 */

import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

/**
 * Forex instrument catalog
 * 
 * Contains 21 major currency pairs organized by:
 * - G10 Developed Markets (majors and crosses)
 * - Emerging Markets (high-yield pairs)
 * - Other (managed/pegged currencies)
 */
export const FOREX_INSTRUMENTS: InstrumentCatalogEntry[] = [
  
  // ===== FOREX CURRENCY PAIRS =====
  
  // MAJORS - G10 Developed Market Currencies
  
  {
    id: 'EURUSD_FOREX',
    symbol: 'EURUSD',
    name: 'Euro/US Dollar',
    shortName: 'EUR/USD',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'EUR',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'USD',
    notes: "Most traded currency pair. Also known as 'Fiber'"
  },
  
  {
    id: 'GBPUSD_FOREX',
    symbol: 'GBPUSD',
    name: 'British Pound/US Dollar',
    shortName: 'GBP/USD',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'GBP',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'USD',
    notes: "Cable - can be volatile"
  },
  
  {
    id: 'USDJPY_FOREX',
    symbol: 'USDJPY',
    name: 'US Dollar/Japanese Yen',
    shortName: 'USD/JPY',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'USD',
    incrementSize: 0.001,
    incrementType: 'pip',
    quoteCurrency: 'JPY',
    notes: 'High liquidity; sensitive to yield differentials'
  },
  
  {
    id: 'USDCHF_FOREX',
    symbol: 'USDCHF',
    name: 'US Dollar/Swiss Franc',
    shortName: 'USD/CHF',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'USD',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'CHF',
    notes: "Safe haven dynamics. Known as 'Swissy'"
  },
  
  {
    id: 'AUDUSD_FOREX',
    symbol: 'AUDUSD',
    name: 'Australian Dollar/US Dollar',
    shortName: 'AUD/USD',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'AUD',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'USD',
    notes: 'Commodity currency. Risk-on sentiment'
  },
  
  {
    id: 'USDCAD_FOREX',
    symbol: 'USDCAD',
    name: 'US Dollar/Canadian Dollar',
    shortName: 'USD/CAD',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'USD',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'CAD',
    notes: 'Loonie. Correlated with oil prices'
  },
  
  {
    id: 'NZDUSD_FOREX',
    symbol: 'NZDUSD',
    name: 'New Zealand Dollar/US Dollar',
    shortName: 'NZD/USD',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'NZD',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'USD',
    notes: 'Kiwi. High yielding commodity currency'
  },
  
  // CROSSES - G10 Currencies vs each other (no USD)
  
  {
    id: 'EURJPY_FOREX',
    symbol: 'EURJPY',
    name: 'Euro/Japanese Yen',
    shortName: 'EUR/JPY',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'EUR',
    incrementSize: 0.001,
    incrementType: 'pip',
    quoteCurrency: 'JPY',
    notes: 'Most liquid cross. Risk sentiment gauge'
  },
  
  {
    id: 'GBPJPY_FOREX',
    symbol: 'GBPJPY',
    name: 'British Pound/Japanese Yen',
    shortName: 'GBP/JPY',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'GBP',
    incrementSize: 0.001,
    incrementType: 'pip',
    quoteCurrency: 'JPY',
    notes: 'High volatility cross. Beast'
  },
  
  {
    id: 'EURGBP_FOREX',
    symbol: 'EURGBP',
    name: 'Euro/British Pound',
    shortName: 'EUR/GBP',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'EUR',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'GBP',
    notes: 'Brexit sensitive. European politics'
  },
  
  {
    id: 'EURAUD_FOREX',
    symbol: 'EURAUD',
    name: 'Euro/Australian Dollar',
    shortName: 'EUR/AUD',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'EUR',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'AUD',
    notes: 'Risk-off/risk-on dynamics'
  },
  
  {
    id: 'EURCHF_FOREX',
    symbol: 'EURCHF',
    name: 'Euro/Swiss Franc',
    shortName: 'EUR/CHF',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'EUR',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'CHF',
    notes: 'SNB intervention history. Range bound'
  },
  
  {
    id: 'AUDCAD_FOREX',
    symbol: 'AUDCAD',
    name: 'Australian Dollar/Canadian Dollar',
    shortName: 'AUD/CAD',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'AUD',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'CAD',
    notes: 'Commodity currency pair'
  },
  
  {
    id: 'AUDNZD_FOREX',
    symbol: 'AUDNZD',
    name: 'Australian Dollar/New Zealand Dollar',
    shortName: 'AUD/NZD',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'AUD',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'NZD',
    notes: 'Antipodean pair. Economic divergence'
  },
  
  {
    id: 'CADJPY_FOREX',
    symbol: 'CADJPY',
    name: 'Canadian Dollar/Japanese Yen',
    shortName: 'CAD/JPY',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'g10'
    },
    contractSize: 100000,
    contractAsset: 'CAD',
    incrementSize: 0.001,
    incrementType: 'pip',
    quoteCurrency: 'JPY',
    notes: 'Oil-sensitive yen cross'
  },
  
  // EMERGING MARKETS
  
  {
    id: 'USDZAR_FOREX',
    symbol: 'USDZAR',
    name: 'US Dollar/South African Rand',
    shortName: 'USD/ZAR',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'em'
    },
    contractSize: 100000,
    contractAsset: 'USD',
    incrementSize: 0.0001,
    incrementType: 'pipette',
    quoteCurrency: 'ZAR',
    notes: 'High volatility EM pair. Political risk'
  },
  
  {
    id: 'USDMXN_FOREX',
    symbol: 'USDMXN',
    name: 'US Dollar/Mexican Peso',
    shortName: 'USD/MXN',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'em'
    },
    contractSize: 100000,
    contractAsset: 'USD',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'MXN',
    notes: 'NAFTA trade sensitive. Oil correlation'
  },
  
  {
    id: 'USDBRL_FOREX',
    symbol: 'USDBRL',
    name: 'US Dollar/Brazilian Real',
    shortName: 'USD/BRL',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'em'
    },
    contractSize: 100000,
    contractAsset: 'USD',
    incrementSize: 0.0001,
    incrementType: 'pipette',
    quoteCurrency: 'BRL',
    notes: 'Commodity exposure. Political volatility'
  },
  
  {
    id: 'USDCNH_FOREX',
    symbol: 'USDCNH',
    name: 'US Dollar/Chinese Yuan Offshore',
    shortName: 'USD/CNH',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'em'
    },
    contractSize: 100000,
    contractAsset: 'USD',
    incrementSize: 0.00001,
    incrementType: 'pipette',
    quoteCurrency: 'CNH',
    notes: 'Trade war sensitivity. PBOC managed'
  },
  
  {
    id: 'USDTRY_FOREX',
    symbol: 'USDTRY',
    name: 'US Dollar/Turkish Lira',
    shortName: 'USD/TRY',
    classification: {
      assetClass: 'forex',
      marketSegment: 'currencies',
      subAssetClass: 'em'
    },
    contractSize: 100000,
    contractAsset: 'USD',
    incrementSize: 0.0001,
    incrementType: 'pipette',
    quoteCurrency: 'TRY',
    notes: 'High volatility. Central bank intervention'
  }
];